/* ════════════════════════════════════════════════════════════════
   USA SUPPLEMENT — SHOPIFY THEME JS
   Production-ready with Shopify Theme Editor lifecycle management
   Vanilla reveals + AJAX Cart + Variant Picker (no external JS libraries)
   ════════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  /* ──────────────────────────────────────
     ACCESSIBILITY: Respect reduced motion
     ────────────────────────────────────── */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ──────────────────────────────────────
     SCROLL REVEAL (vanilla — replaces GSAP)
     JS-gated: elements are only hidden once JS
     runs, so no-JS / failed load = visible.
     ────────────────────────────────────── */
  function initRevealUp(scope) {
    const ctx = scope || document;
    // .usa-reveal and .usa-reveal-stagger are the design-system equivalents of
    // .reveal-up. They set opacity:0 and wait for .is-visible, so if they are
    // not observed here anything using them stays invisible for good.
    const els = ctx.querySelectorAll('.reveal-up, .usa-reveal, .usa-reveal-stagger');
    if (els.length === 0) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(el => {
      if (el.dataset.revealBound) return;
      el.dataset.revealBound = 'true';
      // .reveal-armed is what hides .reveal-up. The design-system classes carry
      // their own hidden state, so arming them again would double the offset.
      if (el.classList.contains('reveal-up')) el.classList.add('reveal-armed');
      // Stagger children in DOM order, so text steps in rather than snapping.
      if (el.classList.contains('usa-reveal-stagger')) {
        Array.prototype.forEach.call(el.children, (child, i) => {
          child.style.setProperty('--i', i);
        });
      }
      io.observe(el);
    });

    // Safety net: reveal anything never scrolled into view.
    setTimeout(() => { els.forEach(el => el.classList.add('is-visible')); }, 2500);
  }

  /* ──────────────────────────────────────
     IMAGE PARALLAX
     Opt in with data-parallax on the wrapper; the value is the strength
     (0.15 default, higher drifts further). Transform only — no layout is
     read or written during the scroll, so it cannot thrash.
     ────────────────────────────────────── */
  function initParallax(scope) {
    const ctx = scope || document;
    const els = Array.prototype.slice.call(ctx.querySelectorAll('[data-parallax]'));
    if (els.length === 0) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

    const active = [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const i = active.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) active.push(entry.target);
        else if (!entry.isIntersecting && i > -1) active.splice(i, 1);
      });
      if (active.length) request();
    }, { rootMargin: '20% 0px 20% 0px' });

    els.forEach(el => {
      if (el.dataset.parallaxBound) return;
      el.dataset.parallaxBound = 'true';
      const media = el.querySelector('img, video');
      // Give the media room to travel, otherwise the drift exposes an edge.
      if (media) media.style.willChange = 'transform';
      io.observe(el);
    });

    let ticking = false;
    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    function update() {
      const vh = window.innerHeight;
      active.forEach(el => {
        const rect = el.getBoundingClientRect();
        const media = el.querySelector('img, video');
        if (!media) return;
        const strength = parseFloat(el.dataset.parallax) || 0.15;
        // -1 above the viewport, 0 centred, 1 below it.
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        const shift = Math.max(-1, Math.min(1, progress)) * rect.height * strength;
        media.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0) scale(' + (1 + strength * 2).toFixed(3) + ')';
      });
      ticking = false;
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    request();
  }

  /* ──────────────────────────────────────
     NAVBAR SCROLL EFFECT
     ────────────────────────────────────── */
  function initNavbar() {
    const nav = document.getElementById('site-nav');
    if (!nav) return;
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 60);
    }, { passive: true });
  }

  /* ──────────────────────────────────────
     MOBILE BOTTOM NAV SCROLL EFFECT
     ────────────────────────────────────── */
  function initMobileBottomNav() {
    const bottomNav = document.querySelector('.mobile-bottom-nav');
    if (!bottomNav) return;
    
    let lastScrollY = window.scrollY;
    
    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      
      // Hide if scrolling down past 100px, show if scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        bottomNav.style.transform = 'translateY(100%)';
      } else {
        bottomNav.style.transform = 'translateY(0)';
      }
      
      lastScrollY = currentScrollY;
    }, { passive: true });
    
    // Add CSS transition via JS to avoid flicker on load
    bottomNav.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
  }

  /* GSAP scroll animations removed — replaced by vanilla initRevealUp() + per-section CSS reveals. */

  /* ──────────────────────────────────────
     ANIMATED COUNTERS (scoped)
     ────────────────────────────────────── */
  function initCounters(scope) {
    const ctx = scope || document;
    const counters = ctx.querySelectorAll('.stat-number[data-target], .vm-stat-number[data-target]');
    if (counters.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.target);
        const suffix = el.dataset.suffix || '';
        if (isNaN(target)) { observer.unobserve(el); return; }

        let current = 0;
        el.textContent = '0' + suffix;

        const duration = 1500; // 1.5s animation duration
        const frameRate = 1000 / 60; // 60fps
        const totalFrames = Math.round(duration / frameRate);
        let frame = 0;

        const timer = setInterval(() => {
          frame++;
          const progress = frame / totalFrames;
          const easeProgress = progress * (2 - progress); // Ease out quad
          current = Math.round(easeProgress * target);

          if (frame >= totalFrames) {
            current = target;
            clearInterval(timer);
          }
          el.textContent = current + suffix;
        }, frameRate);

        observer.unobserve(el);
      });
    }, { threshold: 0.1 });

    counters.forEach(c => observer.observe(c));
  }

  /* ──────────────────────────────────────
     FAQ ACCORDION (scoped)
     ────────────────────────────────────── */
  function initFAQ(scope) {
    const ctx = scope || document;
    ctx.querySelectorAll('.faq-q').forEach(btn => {
      // Avoid duplicate listeners by checking a flag
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', () => {
        const item = btn.parentElement;
        const wasOpen = item.classList.contains('open');
        // Close all FAQ items in this section
        item.closest('.faq-list').querySelectorAll('.faq-item').forEach(i => i.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  /* ──────────────────────────────────────
     JOURNEY TIMELINE (VERTICAL)
     ────────────────────────────────────── */
  function initJourneyTimeline(scope) {
    const ctx = scope || document;
    const timeline = ctx.querySelector('.journey-timeline');
    if (!timeline) return;

    // Filter Logic
    const navBtns = ctx.querySelectorAll('.roadmap-nav-btn');
    const rows = ctx.querySelectorAll('.journey-node-row');

    if (navBtns.length > 0 && rows.length > 0) {
      navBtns.forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          navBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const selectedYear = btn.dataset.year;

          rows.forEach(row => {
            const rowYear = row.dataset.year;
            const show = (selectedYear === 'all' || rowYear === selectedYear);
            if (show) {
              row.classList.remove('hidden');
            } else {
              row.classList.add('hidden');
            }
          });
        });
      });
    }

    // GSAP removed: activate all timeline dots so the roadmap reads correctly.
    ctx.querySelectorAll('.journey-dot').forEach(dot => dot.classList.add('active'));
  }

  /* ──────────────────────────────────────
     PRODUCT GALLERY — Auto-slider (scoped)
     ────────────────────────────────────── */
  function initProductGallery(scope) {
    if (prefersReducedMotion) return;
    const ctx = scope || document;
    const thumbnailsContainer = ctx.querySelector('.pd-thumbnails');
    if (!thumbnailsContainer) return;

    const thumbs = Array.from(thumbnailsContainer.querySelectorAll('.pd-thumb'));
    if (thumbs.length <= 1) return;

    let autoSlideInterval;

    const startAutoSlide = () => {
      autoSlideInterval = setInterval(() => {
        let currentIndex = thumbs.findIndex(t => t.classList.contains('active'));
        if (currentIndex === -1) currentIndex = 0;
        let nextIndex = (currentIndex + 1) % thumbs.length;
        thumbs[nextIndex].click();
      }, 4000);
    };

    startAutoSlide();

    const galleryCol = ctx.querySelector('.pd-gallery-col');
    if (galleryCol) {
      galleryCol.addEventListener('mouseenter', () => clearInterval(autoSlideInterval));
      galleryCol.addEventListener('mouseleave', startAutoSlide);
    }

    // Store interval reference for cleanup on section unload
    if (scope) scope.__autoSlideInterval = autoSlideInterval;
  }

  /* ──────────────────────────────────────
     PRODUCT VARIANT PICKER
     Wires flavor/option buttons to
     Shopify variant IDs and AJAX Cart
     ────────────────────────────────────── */
  function initVariantPicker(scope) {
    const ctx = scope || document;
    const flavorBtns = ctx.querySelectorAll('.pd-flavor-btn');
    if (flavorBtns.length === 0) return;

    flavorBtns.forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        const grid = e.target.closest('.pd-flavor-grid');
        grid.querySelectorAll('.pd-flavor-btn').forEach(b => b.classList.remove('active'));
        e.target.classList.add('active');

        // Build selected options and find matching Shopify variant
        updateVariantFromOptions(ctx);
      });
    });

    // The gallery is built by Swiper after this runs, so sync the preselected
    // variant's image once it exists.
    document.addEventListener('pd:swiper-ready', function () {
      updateVariantFromOptions(ctx);
    }, { once: true });
  }

  function updateVariantFromOptions(ctx) {
    const form = ctx.querySelector('.pd-form');
    if (!form) return;

    // Collect all selected option values
    const selectedOptions = [];
    ctx.querySelectorAll('.pd-option-group').forEach(group => {
      const activeBtn = group.querySelector('.pd-flavor-btn.active');
      if (activeBtn) selectedOptions.push(activeBtn.dataset.optionValue);
    });

    // Find matching variant from the product JSON (if available)
    const productJSON = ctx.querySelector('[data-product-json]');
    if (productJSON) {
      try {
        const product = JSON.parse(productJSON.textContent);
        const matchingVariant = product.variants.find(v => {
          return selectedOptions.every((opt, i) => v.options[i] === opt);
        });
        if (matchingVariant) {
          form.querySelector('input[name="id"]').value = matchingVariant.id;

          // Move the gallery to this variant's image
          showVariantImage(ctx, matchingVariant);

          // Update base price data-attribute
          const priceMainEl = ctx.querySelector('#pd-price-main-el');
          if (priceMainEl) {
            priceMainEl.dataset.basePrice = matchingVariant.price;
          }

          // Update One-time card price text
          const onetimePriceEl = ctx.querySelector('.pd-card-price-onetime');
          if (onetimePriceEl) {
            onetimePriceEl.textContent = formatMoney(matchingVariant.price);
          }

          // Compare-at, per-serving and subscription price all follow the variant.
          updateBuyBoxPrices(ctx, matchingVariant);

          // Recalculate price based on active card (One-time or Subscribe)
          const activeCard = ctx.querySelector('.pd-card-opt.active');
          if (activeCard) {
            updatePriceDisplayFromCard(ctx, activeCard);
          } else {
            const priceEl = ctx.querySelector('.pd-price');
            if (priceEl) priceEl.textContent = formatMoney(matchingVariant.price);
          }

          // Update compare-at price
          const compareEl = ctx.querySelector('.pd-compare-price');
          if (compareEl) {
            if (matchingVariant.compare_at_price > matchingVariant.price) {
              compareEl.innerHTML = '<s>' + formatMoney(matchingVariant.compare_at_price) + '</s>';
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          // Update add-to-cart button state
          const addBtn = ctx.querySelector('.pd-btn-black') || ctx.querySelector('.pd-add-btn');
          const buyNowBtn = ctx.querySelector('#pd-buy-now-btn');
          if (addBtn) {
            if (matchingVariant.available) {
              addBtn.disabled = false;
              if (addBtn.classList.contains('pd-add-btn')) {
                addBtn.textContent = 'Add to Cart — ' + formatMoney(matchingVariant.price);
              } else {
                addBtn.textContent = 'Add to Cart';
              }
            } else {
              addBtn.disabled = true;
              addBtn.textContent = 'Sold Out';
            }
          }
          if (buyNowBtn) {
            buyNowBtn.disabled = !matchingVariant.available;
          }
        }
      } catch (e) {
        console.warn('Variant update failed:', e);
      }
    }
  }

  /* Slide the product gallery to the image assigned to a variant.
     Variants with no image of their own leave the gallery where it is. */
  function showVariantImage(ctx, variant) {
    const image = variant && variant.featured_image;
    if (!image || !image.position) return;

    const swiperEl = ctx.querySelector('.pd-image-swiper') || document.querySelector('.pd-image-swiper');
    if (!swiperEl) return;

    const swiper = window.pdImageSwiper || swiperEl.swiper;
    if (!swiper) return;

    // Autoplay would scroll straight off the flavor the customer just picked.
    if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();

    const target = image.position - 1;
    if (swiper.params.loop) {
      swiper.slideToLoop(target);
    } else {
      swiper.slideTo(target);
    }
  }

  /* Keep the compare-at, per-serving and subscription figures in step with the
     selected variant. Server-rendered on load; this only re-derives them. */
  function updateBuyBoxPrices(ctx, variant) {
    if (!variant) return;
    const cards = ctx.querySelector('.pd-purchase-cards');

    const compareEl = ctx.querySelector('.pd-card-compare');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        compareEl.innerHTML = '<s>' + formatMoney(variant.compare_at_price) + '</s><b>' + pct + '% OFF</b>';
        compareEl.hidden = false;
      } else {
        compareEl.innerHTML = '';
        compareEl.hidden = true;
      }
    }

    const perEl = ctx.querySelector('.pd-price-per');
    const servings = cards ? parseInt(cards.dataset.servings, 10) : 0;
    if (perEl) {
      if (servings > 0) {
        const per = Math.round(variant.price / servings / 100) * 100;
        perEl.textContent = formatMoney(per) + ' ' + (cards.dataset.perServingSuffix || 'per serving');
        perEl.hidden = false;
      } else {
        perEl.hidden = true;
      }
    }

    const subEl = ctx.querySelector('.pd-card-sub-price');
    const subCard = ctx.querySelector('.pd-card-opt[data-purchase-type="subscribe"]');
    if (subEl && subCard) {
      const sub = subscriptionPrice(subCard, variant.price);
      if (sub === null) { subEl.hidden = true; } else { subEl.textContent = formatMoney(sub); subEl.hidden = false; }
    }
  }

  /* The subscription price comes from the selling plan's own adjustment, never a
     hardcoded percentage — the card must not advertise a saving Shopify will not
     actually apply at checkout. */
  function subscriptionPrice(card, basePrice) {
    const type = card.dataset.adjType;
    const value = parseFloat(card.dataset.adjValue);
    if (!type || isNaN(value)) return null;
    if (type === 'percentage') return Math.round(basePrice * (100 - value) / 100);
    if (type === 'fixed_amount') return Math.max(0, basePrice - value);
    return value; // 'price' — the plan sets an absolute price
  }

  function updatePriceDisplayFromCard(ctx, card) {
    const sellingPlanInput = ctx.querySelector('#pd-selling-plan-input');
    const purchaseType = card.dataset.purchaseType;

    /* Attaching the plan id is the whole point of this function, so it happens
       first and unconditionally. It used to sit behind an early return on a
       price element that no longer exists in the markup, which meant clicking
       "Subscribe & Save" added a plain one-time item at full price. */
    if (sellingPlanInput) {
      sellingPlanInput.value = purchaseType === 'subscribe' ? (card.dataset.sellingPlanId || '') : '';
    }

    const priceMainEl = ctx.querySelector('#pd-price-main-el');
    if (!priceMainEl) return;

    const basePrice = parseInt(priceMainEl.dataset.basePrice) || 0;
    if (purchaseType === 'subscribe') {
      const discounted = subscriptionPrice(card, basePrice);
      priceMainEl.textContent = formatMoney(discounted === null ? basePrice : discounted);
    } else {
      priceMainEl.textContent = formatMoney(basePrice);
    }
  }

  async function handleBuyNow(ctx, btn) {
    const form = btn.closest('.pd-form');
    if (!form) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Processing...';

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        window.location.href = '/checkout';
      } else {
        const error = await response.json();
        btn.textContent = error.description || 'Error — Try Again';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      window.location.href = '/checkout';
    }
  }

  function initPurchaseControls(scope) {
    const ctx = scope || document;
    
    // Purchase Card Options (One-time vs Subscription)
    const cards = ctx.querySelectorAll('.pd-card-opt:not(.disabled-plan)');
    cards.forEach(card => {
      if (card.dataset.bound) return;
      card.dataset.bound = 'true';
      card.addEventListener('click', () => {
        const container = card.closest('.pd-purchase-cards');
        if (container) {
          container.querySelectorAll('.pd-card-opt').forEach(c => c.classList.remove('active'));
        }
        card.classList.add('active');
        updatePriceDisplayFromCard(ctx, card);
      });
    });

    /* Both markups are bound: the buy box renders `.pd-qty-box-full`, which never
       matched the `.pd-qty-box` selector, so the +/- buttons did nothing and the
       readonly input was stuck at 1. */
    const qtyBoxes = ctx.querySelectorAll('.pd-qty-box, .pd-qty-box-full');
    qtyBoxes.forEach(box => {
      const input = box.querySelector('input');
      const decBtn = box.querySelector('[data-action="decrease"]');
      const incBtn = box.querySelector('[data-action="increase"]');
      if (!input || !decBtn || !incBtn) return;

      if (!decBtn.dataset.bound) {
        decBtn.dataset.bound = 'true';
        decBtn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (val > 1) input.value = val - 1;
        });
      }

      if (!incBtn.dataset.bound) {
        incBtn.dataset.bound = 'true';
        incBtn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (val < 99) input.value = val + 1;
        });
      }
    });

    // Buy Now click trigger
    const buyNowBtn = ctx.querySelector('#pd-buy-now-btn');
    if (buyNowBtn && !buyNowBtn.dataset.bound) {
      buyNowBtn.dataset.bound = 'true';
      buyNowBtn.addEventListener('click', () => {
        handleBuyNow(ctx, buyNowBtn);
      });
    }
  }

  /* The symbol is read off the server-rendered price rather than hardcoded, so a
     price the theme rewrites in JS keeps the same currency mark the Liquid
     `money` filter printed. Hardcoding "Rs. " made a flavour switch silently
     change the currency mark on the page. */
  let _moneyPrefix = null;
  function moneyPrefix() {
    if (_moneyPrefix !== null) return _moneyPrefix;
    const el = document.querySelector('.pd-card-price-onetime, .pcard-price, .pd-price');
    const match = el ? el.textContent.trim().match(/^[^\d]*/) : null;
    _moneyPrefix = match && match[0] ? match[0] : 'Rs. ';
    return _moneyPrefix;
  }

  function formatMoney(cents) {
    return moneyPrefix() + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ──────────────────────────────────────
     QUANTITY SELECTOR (scoped)
     ────────────────────────────────────── */
  function initQuantitySelector(scope) {
    const ctx = scope || document;
    ctx.querySelectorAll('.pd-qty').forEach(container => {
      const btns = container.querySelectorAll('.pd-qty-btn');
      const input = container.querySelector('.pd-qty-input');
      if (!input || btns.length < 2) return;

      btns.forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (btn.dataset.action === 'decrease' && val > 1) input.value = val - 1;
          if (btn.dataset.action === 'increase' && val < 99) input.value = val + 1;
        });
      });
    });
  }

  /* ──────────────────────────────────────
     AJAX CART — Add to Cart via Fetch API
     Uses Shopify's /cart/add.js endpoint
     ────────────────────────────────────── */
  function initAJAXCart(scope) {
    const ctx = scope || document;
    const forms = ctx.querySelectorAll('.pd-form');

    forms.forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('.pd-btn-black') || form.querySelector('.pd-add-btn') || form.querySelector('button[type="submit"]');
        if (!submitBtn || submitBtn.disabled) return;

        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding...';
        submitBtn.disabled = true;

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            submitBtn.textContent = '✓ Added to Cart!';
            submitBtn.style.background = 'var(--usa-green)';

            // Update cart count in header
            updateCartCount();

            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.style.background = '';
              submitBtn.disabled = false;
            }, 2000);
          } else {
            const error = await response.json();
            submitBtn.textContent = error.description || 'Error — Try Again';
            submitBtn.style.background = 'var(--usa-red)';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.style.background = '';
              submitBtn.disabled = false;
            }, 2500);
          }
        } catch (err) {
          console.error('Cart add error:', err);
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('.cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (e) { /* silent fail */ }
  }

  /* ──────────────────────────────────────
     NEWSLETTER FORM
     ────────────────────────────────────── */
  function initNewsletter(scope) {
    const ctx = scope || document;
    const form = ctx.querySelector('#newsletter-form');
    if (!form || form.dataset.bound) return;
    form.dataset.bound = 'true';

    form.addEventListener('submit', (e) => {
      // On Shopify, the native form will POST to /contact
      // This provides visual feedback before redirect
      const input = form.querySelector('input[type="email"]');
      if (input && input.value) {
        // Let the form submit naturally to Shopify
        // but show a quick visual cue
        const btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.textContent = '✓ Subscribed!';
          btn.style.background = 'var(--usa-green)';
        }
      }
    });
  }

  /* ──────────────────────────────────────
     MICRO-INTERACTIONS (global, once)
     Magnetic buttons + 3D tilt + spotlight
     ────────────────────────────────────── */
  function initMicroInteractions() {
    if (prefersReducedMotion) return;

    // Magnetic Buttons
    document.querySelectorAll('.btn').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = `translate(0px, 0px)`;
      });
    });

    // 3D Tilt on Product Cards
    document.querySelectorAll('.product-card, .collection-product-card, .upcoming-card').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const rotateX = ((y - rect.height / 2) / (rect.height / 2)) * -4;
        const rotateY = ((x - rect.width / 2) / (rect.width / 2)) * 4;
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-5px)`;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)`;
      });
    });

    // Cursor spotlight tracking
    document.addEventListener('mousemove', (e) => {
      const selector = '.spotlight-card, .vm-card, .pillar-tile, .product-card, .collection-product-card, .upcoming-card, .why-card, .stat-card, .roadmap-card';
      document.querySelectorAll(selector).forEach(card => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      });
    }, { passive: true });
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  /* ════════════════════════════════════════
     GLOBAL IMAGE HELPER
     Called by onclick in product thumbnails
     ════════════════════════════════════════ */
  window.changeMainImage = function(thumbEl, fullSrc) {
    const mainImg = document.getElementById('pdMainImage');
    if (mainImg) {
      mainImg.style.opacity = '0';
      mainImg.style.transform = 'translateX(-15px)';
      
      setTimeout(() => {
        mainImg.src = fullSrc;
        mainImg.style.transform = 'translateX(15px)';
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            mainImg.style.opacity = '1';
            mainImg.style.transform = 'translateX(0)';
          });
        });
      }, 250);
    }
    // Update active state
    const container = thumbEl.closest('.pd-thumbnails');
    if (container) {
      container.querySelectorAll('.pd-thumb').forEach(t => t.classList.remove('active'));
      thumbEl.classList.add('active');
    }
  };

  /* ════════════════════════════════════════
     MOBILE NAVIGATION DRAWER
     ════════════════════════════════════════ */
  function initMobileDrawer(scope) {
    const ctx = scope || document;
    const toggle = ctx.querySelector('.mobile-menu-toggle');
    const close = ctx.querySelector('.mobile-drawer-close');
    const overlay = ctx.querySelector('.mobile-drawer-overlay');
    const drawer = ctx.querySelector('.mobile-drawer');
    const links = ctx.querySelectorAll('.mobile-nav-links a');

    if (!toggle || !drawer) return;

    const openDrawer = () => {
      drawer.classList.add('open');
      document.body.classList.add('drawer-open');
    };

    const closeDrawer = () => {
      drawer.classList.remove('open');
      document.body.classList.remove('drawer-open');
    };

    if (!toggle.dataset.bound) {
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (drawer.classList.contains('open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    if (close && !close.dataset.bound) {
      close.dataset.bound = 'true';
      close.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (overlay && !overlay.dataset.bound) {
      overlay.dataset.bound = 'true';
      overlay.addEventListener('click', closeDrawer);
    }

    links.forEach(link => {
      if (!link.dataset.bound) {
        link.dataset.bound = 'true';
        link.addEventListener('click', closeDrawer);
      }
    });
  }

  // ═══ AUTO-SLIDING PRODUCTS CAROUSEL (MOBILE) ═══
  function initProductAutoplay(scope) {
    if (prefersReducedMotion) return;
    const ctx = scope || document;
    const grid = ctx.querySelector ? ctx.querySelector('.products-grid') : document.querySelector('.products-grid');
    if (!grid) return;

    let scrollInterval;

    const startAutoplay = () => {
      clearInterval(scrollInterval);
      scrollInterval = setInterval(() => {
        if (window.innerWidth > 992) return;
        
        const card = grid.querySelector('.product-card');
        if (!card) return;
        const cardWidth = card.offsetWidth;
        const gap = 16; // gap between cards on mobile
        const step = cardWidth + gap;
        
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        if (grid.scrollLeft >= maxScroll - 5) {
          grid.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          grid.scrollBy({ left: step, behavior: 'smooth' });
        }
      }, 3000);
    };

    startAutoplay();

    // Stop autoplay on user interaction
    const stopAutoplay = () => clearInterval(scrollInterval);
    grid.addEventListener('touchstart', stopAutoplay, { passive: true });
    grid.addEventListener('mousedown', stopAutoplay, { passive: true });

    // Resume autoplay after user interaction stops
    let resumeTimeout;
    const interactionEnd = () => {
      clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(startAutoplay, 5000);
    };
    grid.addEventListener('touchend', interactionEnd, { passive: true });
    grid.addEventListener('mouseup', interactionEnd, { passive: true });

    // Store interval on the grid element so we can clean it up on unload
    grid.__productAutoplayInterval = scrollInterval;
  }

  /* ════════════════════════════════════════
     MASTER INIT — Called on DOMContentLoaded
     ════════════════════════════════════════ */
  function initAll(scope) {
    initMobileDrawer(scope);
    initRevealUp(scope);
    initParallax(scope);
    initCounters(scope);
    initFAQ(scope);
    initJourneyTimeline(scope);
    initProductGallery(scope);
    initVariantPicker(scope);
    initQuantitySelector(scope);
    initPurchaseControls(scope);
    initAJAXCart(scope);
    initNewsletter(scope);
    initProductAutoplay(scope);
  }

  /* ════════════════════════════════════════
     EVENT LISTENERS
     ════════════════════════════════════════ */

  // Initial page load
  document.addEventListener('DOMContentLoaded', () => {
    initNavbar();
    initMobileBottomNav();
    initAll();
    initMicroInteractions();
  });

  /* ════════════════════════════════════════
     SHOPIFY THEME EDITOR LIFECYCLE HOOKS
     
     These events fire when a merchant:
     - Adds/removes/reorders sections in Customize
     - Changes section settings
     - Clicks into a section to edit it
     
     Without these, GSAP animations break,
     event listeners leak, and intervals orphan.
     ════════════════════════════════════════ */

  // A section was added or reloaded in the Theme Editor
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    initAll(section);
  });

  // A section was removed — clean up to prevent memory leaks
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target;

    // Kill any auto-slide intervals attached to this section
    if (section.__autoSlideInterval) {
      clearInterval(section.__autoSlideInterval);
    }
    const grid = section.querySelector('.products-grid');
    if (grid && grid.__productAutoplayInterval) {
      clearInterval(grid.__productAutoplayInterval);
    }
  });

  // A section was selected (clicked) in the Theme Editor
  document.addEventListener('shopify:section:select', (e) => {
    // Scroll the section into view smoothly
    e.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // A block was selected in the Theme Editor (e.g., a specific FAQ item)
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;

    // If it's a FAQ item, open it
    if (block.classList.contains('faq-item')) {
      block.classList.add('open');
    }

    // Scroll block into view
    block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // A block was deselected
  document.addEventListener('shopify:block:deselect', (e) => {
    const block = e.target;
    if (block.classList.contains('faq-item')) {
      block.classList.remove('open');
    }
  });

})();
